import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Dropdown } from "react-bootstrap";
import TablePagination from "./TablePagination";
import { getRatingList, updateRatingStatus } from "../Apis/RatingAPIs";
import { toast } from "react-toastify";

const FeedbackLayer = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchRatings = async () => {
        setLoading(true);
        const res = await getRatingList();
        if (res.status) {
            const fetchedData = res.response?.data || [];
            setData(fetchedData);
            setTotalRecords(fetchedData.length);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchRatings();
    }, []);

    // Filter data based on search term
    const filteredData = data.filter(item =>
        (item.comment || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.expoId?.expoName || "").toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const startIndex = currentPage * rowsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

    // Update total records when filter changes
    useEffect(() => {
        setTotalRecords(filteredData.length);
        setCurrentPage(0); // reset to first page on search
    }, [searchTerm, data]);

    // Handle status change from dropdown
    const handleStatusChange = async (id, newStatus) => {
        try {
            const res = await updateRatingStatus(id, newStatus);
            if (res.status) {
                toast.success(`Status updated to ${newStatus}`);
                fetchRatings(); // Refresh data
            } else {
                toast.error(res.response?.message || "Failed to update status");
            }
        } catch (error) {
            toast.error("An error occurred");
        }
    };

    // Helper to render star rating
    const renderStars = (rating) => {
        return (
            <div className="d-flex gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                    <Icon
                        key={star}
                        icon={star <= rating ? "mdi:star" : "mdi:star-outline"}
                        className="text-lg"
                        style={{ color: star <= rating ? "#ffc107" : "#e4e5e9" }}
                    />
                ))}
            </div>
        );
    };

    // Helper for status badge colors
    const getStatusColor = (status) => {
        const s = (status || "").toLowerCase();
        switch (s) {
            case "approved": return "success";
            case "denied": return "danger";
            default: return "warning";
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}>
                <h6 className="text-white pb-2 mb-0">Feedback Management</h6>
                <div className="d-flex align-items-center gap-3">
                    <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            placeholder="Search reviews..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ color: "black" }}
                        />
                        <Icon icon="ion:search-outline" className="icon" style={{ color: "black" }} />
                    </form>
                </div>
            </div>
            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr style={{ backgroundColor: "#f7e7da" }}>
                                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Expo Name</th>
                                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Rating</th>
                                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Review</th>
                                <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" className="text-center py-4">Loading...</td></tr>
                            ) : paginatedData.length > 0 ? (
                                paginatedData.map((item, index) => (
                                    <tr key={item._id || item.id}>
                                        <td>{startIndex + index + 1}</td>
                                        <td>{item.expoId?.expoName || "N/A"}</td>
                                        <td>{renderStars(item.rating)}</td>
                                        <td style={{ maxWidth: "300px", wordBreak: "break-word" }}>{item.comment}</td>
                                        <td>
                                        <Dropdown onSelect={(eventKey) => handleStatusChange(item._id || item.id, eventKey)}>
                                            <Dropdown.Toggle
                                                variant={getStatusColor(item.status)}
                                                size="sm"
                                                className="d-flex align-items-center justify-content-center gap-1"
                                                style={{ width: "130px" }}
                                            >
                                                {item.status?.toLowerCase() === 'approved' && <Icon icon="mdi:check-circle" width="16" />}
                                                {item.status?.toLowerCase() === 'denied' && <Icon icon="mdi:close-circle" width="16" />}
                                                {(item.status?.toLowerCase() === 'pending' || !item.status) && <Icon icon="mdi:clock-outline" width="16" />}
                                                {(item.status || "Pending").charAt(0).toUpperCase() + (item.status || "pending").slice(1)}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item eventKey="Approved" className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:check-circle" width="18" height="18" className="text-success" />
                                                    <span>Approved</span>
                                                </Dropdown.Item>
                                                <Dropdown.Item eventKey="Denied" className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:close-circle" width="18" height="18" className="text-danger" />
                                                    <span>Denied</span>
                                                </Dropdown.Item>
                                                <Dropdown.Item eventKey="Pending" className="d-flex align-items-center gap-2">
                                                    <Icon icon="mdi:clock-outline" width="18" height="18" className="text-warning" />
                                                    <span>Pending</span>
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="5" className="text-center py-4">No matching reviews found</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <TablePagination
                    currentPage={currentPage}
                    totalPages={Math.ceil(totalRecords / rowsPerPage)}
                    onPageChange={setCurrentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                    totalRecords={totalRecords}
                />
            </div>
        </div>
    );
};

export default FeedbackLayer;